import { useEffect } from "react";

/**
 * Lightweight title/meta-tag manager. Avoids pulling in react-helmet
 * for three legal pages. Sets <title>, meta description, and a
 * <link rel="canonical"> when the component mounts; cleans up on
 * unmount so we don't leak stale meta tags into other routes.
 */
export default function PageMeta({ title, description, canonical, jsonLd, ogImage }) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const ensureMeta = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      const created = !el;
      if (!el) {
        el = document.createElement(selector.startsWith("link") ? "link" : "meta");
        document.head.appendChild(el);
      }
      const prev = {};
      Object.entries(attrs).forEach(([k, v]) => {
        prev[k] = el.getAttribute(k);
        if (v == null) el.removeAttribute(k);
        else el.setAttribute(k, v);
      });
      return { el, prev, created };
    };

    const cleanups = [];
    if (description) {
      const { el, prev, created } = ensureMeta('meta[name="description"]', {
        name: "description",
        content: description,
      });
      cleanups.push(() => {
        if (created) el.remove();
        else if (prev.content != null) el.setAttribute("content", prev.content);
      });
    }
    if (canonical) {
      const { el, prev, created } = ensureMeta('link[rel="canonical"]', {
        rel: "canonical",
        href: canonical,
      });
      cleanups.push(() => {
        if (created) el.remove();
        else if (prev.href != null) el.setAttribute("href", prev.href);
      });
    }

    // Keep Open Graph tags in sync with the page-specific meta so shared
    // links carry the page's own title/description instead of the homepage's.
    const ogPairs = [
      ["og:title", title],
      ["og:description", description],
      ["og:url", canonical],
      ["og:image", ogImage],
    ];
    ogPairs.forEach(([prop, value]) => {
      if (!value) return;
      const { el, prev, created } = ensureMeta(`meta[property="${prop}"]`, {
        property: prop,
        content: value,
      });
      cleanups.push(() => {
        if (created) el.remove();
        else if (prev.content != null) el.setAttribute("content", prev.content);
      });
    });

    if (jsonLd) {
      const blocks = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      blocks.forEach((block) => {
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.setAttribute("data-page-meta", "1");
        script.textContent = JSON.stringify(block);
        document.head.appendChild(script);
        cleanups.push(() => script.remove());
      });
    }

    return () => {
      document.title = prevTitle;
      cleanups.forEach((fn) => fn());
    };
  }, [title, description, canonical, jsonLd, ogImage]);

  return null;
}
